import React, { useState } from 'react';
import { IonRouterOutlet } from '@ionic/react';
import { Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts';
import LoginReg from '../pages/login/LoginReg';
import { RoleBasedTabs } from './RoleBasedTabs';
import RoleBasedTabsDesktop from '../desktop/routing/RoleBasedTabsDesktop';
import DesktopNotFound from '../desktop/pages/unauthenticated/DesktopNotFound';
import DesktopUnauthorized from '../desktop/pages/unauthenticated/DesktopUnauthorized';
import TeamTabs from './TeamTabs';
import { ProtectedRoute } from './ProtectedRoute';
import { AuthGuard } from './AuthGuard';
// TeamMessagingPage replaced by TeamTabs
import { OWNER_ROLES, MANAGER_ROLES, STAFF_ROLES } from '@rsmaids/shared/constants/roles';
import Unauthorized from '../pages/unauthenticated/Unauthorized';
import NotFound from '../pages/unauthenticated/NotFound';
import GoogleCalendarCallback from '../pages/GoogleCalendarCallback';
import OutlookCalendarCallback from '../pages/OutlookCalendarCallback';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';

const TAB_ROOT = '/tabs';
const IS_DESKTOP = import.meta.env.VITE_PLATFORM === 'desktop';

// Resolved at build/transform time by Vite's define
const TabsComponent = import.meta.env.VITE_PLATFORM === 'desktop'
  ? DesktopTabsWithAuthGuard
  : TabsWithAuthGuard;

console.log('[MainRoute] VITE_PLATFORM =', import.meta.env.VITE_PLATFORM, '| TabsComponent =', TabsComponent.name);



function TabsWithAuthGuard() {
  return (
    <AuthGuard>
      <RoleBasedTabs />
    </AuthGuard>
  );
}
 
function DesktopTabsWithAuthGuard() {
  return (
    <AuthGuard>
      <RoleBasedTabsDesktop />
    </AuthGuard>
  );
}

function RedirectToTabContact() {
  const { id } = useParams();
  return <Navigate to={`${TAB_ROOT}/contact/${id}`} replace />;
}

function RedirectToTabContactEdit() {
  const { id } = useParams();
  return <Navigate to={`${TAB_ROOT}/contact/edit/${id}`} replace />;
}

const MainRoute: React.FC = () => {
  const { authState } = useAuth();
  const { isLoggedIn, isLoading } = authState;
  const location = useLocation();
  // Render-visible: read below to populate the 404 redirect's `state.from`,
  // so it must be real state — a ref mutation here wouldn't itself trigger
  // the re-render needed to reflect the latest known-good route.
  const [lastValidRoute, setLastValidRoute] = useState<string>(isLoggedIn ? `${TAB_ROOT}/dashboard` : '/login');
  const outletKey = IS_DESKTOP ? 'desktop' : 'main';

  // Tracking the last valid route is a pure synchronous derivation of the
  // current location, computed during render (React's "adjust state when a
  // dependency changes" pattern) rather than as a setState call inside an
  // effect.
  const [prevLocation, setPrevLocation] = useState(location);
  if (prevLocation !== location) {
    setPrevLocation(location);
    const pathname = location.pathname;
    const isKnownPath =
      pathname === '/' ||
      pathname === '/login' ||
      pathname === '/404' ||
      pathname === '/unauthorized' ||
      pathname === '/privacy' ||
      pathname === '/terms' ||
      pathname === '/google-calendar-callback' ||
      pathname === '/outlook-calendar-callback' ||
      pathname === '/profile' ||
      pathname === '/dashboard' ||
      pathname === '/calendar' ||
      pathname === '/contacts' ||
      pathname.startsWith('/contact/') ||
      pathname.startsWith('/tabs') ||
      pathname.startsWith('/team');

    if (isKnownPath && pathname !== '/404' && pathname !== '/unauthorized') {
      setLastValidRoute(`${location.pathname}${location.search}${location.hash}`);    }
  }

  return (
    <IonRouterOutlet key={outletKey}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginReg />} />
      <Route path="/profile" element={<Navigate to={`${TAB_ROOT}/profile`} replace />} />
      <Route path="/dashboard/owner/profile" element={<Navigate to={`${TAB_ROOT}/profile`} replace />} />
      <Route path="/dashboard/client/profile" element={<Navigate to={`${TAB_ROOT}/profile`} replace />} />
      <Route path="/dashboard/client/alerts" element={<Navigate to={`${TAB_ROOT}/dashboard/client/alerts`} replace />} />
      <Route path="/estimates" element={<Navigate to={`${TAB_ROOT}/estimates`} replace />} />
      <Route path="/dashboard" element={<Navigate to={`${TAB_ROOT}/dashboard`} replace />} />
      <Route path="/calendar" element={<Navigate to={import.meta.env.VITE_PLATFORM === 'desktop' ? `${TAB_ROOT}/schedule` : `${TAB_ROOT}/calendar`} replace />} />
      <Route path="/contacts" element={<Navigate to={`${TAB_ROOT}/contacts`} replace />} />
      <Route path="/contact/:id" element={<RedirectToTabContact />} />
      <Route path="/contact/new" element={<Navigate to={`${TAB_ROOT}/contact/new`} replace />} />
      <Route path="/contact/edit/:id" element={<RedirectToTabContactEdit />} />

      <Route path="/unauthorized" element={IS_DESKTOP ? <DesktopUnauthorized /> : <Unauthorized />} />
      <Route path="/404" element={IS_DESKTOP ? <DesktopNotFound /> : <NotFound />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      {/* Public customer subscription checkout — no auth required */}


      {/* Google Calendar OAuth callback — must be accessible without tab navigation */}
      <Route path="/google-calendar-callback" element={<GoogleCalendarCallback />} />
      <Route path="/outlook-calendar-callback" element={<OutlookCalendarCallback />} />

      {/* Protected app routes — desktop build uses desktop tabs, mobile uses mobile tabs */}

      <Route path="/tabs/*" element={<TabsComponent />} />

      {/* Redirect legacy dashboard link to team tabs */}
      <Route path="/dashboard/team-messaging" element={<Navigate to="/team/messages" replace />} />

      {/* Team tabs - separate tab set for team features */}
      <Route
        path="/team/*"
        element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <TeamTabs />
          </ProtectedRoute>
        }
      />

      {/* Root redirect */}
      <Route path="/" element={
        isLoggedIn
          ? <Navigate to={`${TAB_ROOT}/dashboard`} replace />
          : <Navigate to="/login" replace />
      } />

      {/* Catch-all for 404 errors */}
      <Route path="*" element={
        isLoading || location.pathname === '/' || location.pathname.startsWith('/tabs') || location.pathname.startsWith('/desktop-tabs') || location.pathname.startsWith('/team') || location.pathname === '/login' || location.pathname === '/unauthorized'
          ? null
          : (
            <Navigate
              to="/404"
              replace
              state={IS_DESKTOP ? { from: lastValidRoute } : undefined}
            />
          )
      } />
    </Routes>
    </IonRouterOutlet>
  );
};

export default MainRoute;

/*
=== COMMENTED ROUTES TO INTEGRATE INTO NEW TAB STRUCTURE ===




Other dashboard routes:
<ProtectedRoute
  path="/dashboard/team-messaging"
  exact
  component={TeamMessagingPage}
  requiredRoles={['owner', 'manager', 'lead', 'staff']}
/>



Client routes:
<Route exact path="/bookingSystem">
  <Redirect to="/bookingSystem/booking" />
</Route>

<PrivateRoute
  exact
  path="/bookingSystem/booking"
  component={ClientBooking}
  allowedRoles={['client']}
/>
<PrivateRoute
  exact
  path="/bookingSystem/contact"
  component={ClientContact}
  allowedRoles={['client']}
/>

Inbox messaging routes:
<ProtectedRoute
  path="/inbox/:conversationId"
  exact
  component={Inbox}
  requiredRoles={['owner', 'manager', 'lead', 'staff']}
/>

Root redirect (alternative):
<Route exact path="/">
  {isAuthenticated ? <Redirect to="/app" /> : <Redirect to="/login" />}
</Route>

---

COMMENTED OUT IMPORTS - Reference for building new tab structure:
import _TeamMessagingPage from './pages/TeamMessagingPage';
import _AlertsPage from './pages/AlertsPage';


*/
